import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

export type ConfettiProps = {
  /** Bump this to trigger a burst. The component listens for changes. */
  trigger: number | string;
  /** Particle count. Default 18 -- enough to feel celebratory, not noisy. */
  count?: number;
  /** Spread radius in px. Default 90. */
  spread?: number;
  /** Override colours -- default is brand orange + warm complements. */
  colors?: string[];
  /** Position the burst origin relative to the parent. Default centre. */
  origin?: { x: string; y: string };
  /** Optional 1-line caption that rises with the burst. */
  label?: string;
};

const DEFAULT_COLORS = ["#F97316", "#FB923C", "#FED7AA", "#FACC15", "#34D399"];

/**
 * Brand-tinted confetti burst. Lives inside a `relative` parent and
 * fires from the origin (default centre) outward in a 360deg pattern
 * with randomised distance, rotation, and decay. ~700ms one-shot.
 *
 * Designed for reward moments (streak crosses 7/14/30, daily question
 * correct, chapter complete) -- not for everyday actions. The plan
 * permits "expressive moments for rewards" -- this is them.
 *
 * Respects prefers-reduced-motion -- in that mode the burst is replaced
 * with a single quick fade of the optional label and no particles, so
 * the moment is still acknowledged without motion.
 */
export function Confetti({
  trigger,
  count = 18,
  spread = 90,
  colors = DEFAULT_COLORS,
  origin = { x: "50%", y: "50%" },
  label,
}: ConfettiProps) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<number | string | null>(null);

  useEffect(() => {
    if (trigger == null || trigger === 0 || trigger === "") return;
    setActive(trigger);
    const t = window.setTimeout(() => setActive(null), 1100);
    return () => window.clearTimeout(t);
  }, [trigger]);

  if (active == null) return null;

  // Deterministic-ish per-burst particle field. Math.random() at mount
  // is fine -- this is one-shot anyway.
  const particles = Array.from({ length: count }, () => ({
    angle: Math.random() * Math.PI * 2,
    dist: 0.4 + Math.random() * 0.9, // 0.4..1.3 of spread
    color: colors[Math.floor(Math.random() * colors.length)],
    rot: -180 + Math.random() * 360,
    size: 5 + Math.random() * 6,
    delay: Math.random() * 0.08,
  }));

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      <AnimatePresence>
        <div
          key={String(active)}
          style={{ position: "absolute", left: origin.x, top: origin.y }}
        >
          {reduce ? null : particles.map((p, i) => {
            const x = Math.cos(p.angle) * spread * p.dist;
            const y = Math.sin(p.angle) * spread * p.dist;
            return (
              <motion.span
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, rotate: 0, scale: 0.4 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x,
                  y,
                  rotate: p.rot,
                  scale: 1,
                }}
                transition={{
                  duration: 0.7,
                  delay: p.delay,
                  ease: [0.2, 0.7, 0.4, 1],
                  opacity: { times: [0, 0.1, 0.7, 1], duration: 0.7, delay: p.delay },
                }}
                style={{
                  position: "absolute",
                  width: p.size,
                  height: p.size,
                  borderRadius: "20%",
                  background: p.color,
                  transformOrigin: "center",
                  willChange: "transform, opacity",
                }}
              />
            );
          })}

          {label ? (
            <motion.span
              className="absolute left-1/2 -translate-x-1/2 -top-2 whitespace-nowrap text-xs font-medium text-brand bg-bg/85 border border-brand/30 px-2 py-0.5 rounded-full backdrop-blur"
              initial={{ opacity: 0, y: 4, scale: 0.9 }}
              animate={{ opacity: 1, y: -10, scale: 1 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.5, ease: [0.2, 0.7, 0.4, 1] }}
            >
              {label}
            </motion.span>
          ) : null}
        </div>
      </AnimatePresence>
    </div>
  );
}
